import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AppCheckboxField, AppEquipmentTypeSelectField } from '@/form/FormUI';
import type { CreateEquipmentNodeType, NodeFormProps } from '@/nodes';
import { equipmentTypesState, useSavedFormValues } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateEquipmentDataSchema, type CreateEquipmentDataType } from '@rock/types';
import { type FC, useCallback, useEffect } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

type FormInput = z.input<typeof CreateEquipmentDataSchema>;

const CreateEquipmentForm: FC<NodeFormProps<CreateEquipmentNodeType>> = ({
    spaceId,
    flowId,
    node,
    updateNode,
    cancelUpdate,
}) => {
    const equipmentTypes = useAsyncAtomValue(equipmentTypesState(spaceId));
    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, CreateEquipmentDataType>({
        resolver: zodResolver(CreateEquipmentDataSchema),
        defaultValues: formState || node.data,
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const onSubmit = useCallback(
        (values: CreateEquipmentDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors<FormInput>) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <AppEquipmentTypeSelectField
                        control={form.control}
                        name="equipmentTypeId"
                        label="Equipment Type"
                        placeholder="Select Equipment Type"
                        description="This is the Equipment Type that will be created at this node (should match previous nodes that place info in context)"
                        equipmentTypes={equipmentTypes}
                        allowContextSelection
                    />
                    <AppCheckboxField
                        control={form.control}
                        name="resetValuesAfterCreate"
                        label="Reset Values After Create"
                        description="Whether to clear the equipment data in context after creation"
                    />
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};

export default CreateEquipmentForm;
